import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/material/Stack';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import React, { useCallback, useEffect, useState } from 'react';
import { AddDocuments } from 'services/DocumentsService';
import FilePicker from 'components/Pickers/FilePicker';
import RWTextFieldComponent from 'components/RWTextFieldComponent';
import { DocumentType } from 'common/models/Document';
import DocumentTypePicker from 'components/Pickers/DocumentTypePicker';

interface Props {
  open: boolean;
  entityTypeId: number;
  playerId: number;
  closeHandler: () => void;
  loadingHandler?: (isLoading: boolean) => void;
  errorHandler?: (message: string) => void;
  successHandler?: (message: string) => void;
}

export default function UploadDocumentsDialog({
  open,
  entityTypeId,
  playerId,
  closeHandler,
  loadingHandler,
  errorHandler,
  successHandler,
}: Props) {
  const [filesToUpload, setFilesToUpload] = useState<File[]>([]);
  const [selectedType, setSelectedType] = useState<DocumentType | null>(null);
  const [expirationDate, setExpirationDate] = useState<moment.Moment | null>(null);
  const [notes, setNotes] = useState('');

  useEffect(() => {
    if (!open) {
      setFilesToUpload([]);
      setSelectedType(null);
      setExpirationDate(null);
    }
  }, [open]);

  const addFilesCallback = useCallback((files: File[]) => {
    setFilesToUpload((prev) => [...prev, ...files]);
  }, []);

  const removeFileCallback = useCallback((index: number) => {
    setFilesToUpload((prev) => {
      const files = [...prev];
      files.splice(index, 1);
      return files;
    });
  }, []);

  const uploadDocumentsCallback = useCallback(async () => {
    loadingHandler && loadingHandler(true);
    let res: boolean | null = null;
    const expDate =
      expirationDate && expirationDate.isValid()
        ? expirationDate.format('YYYY-MM-DD')
        : '0001-01-01';
    res = await AddDocuments(
      entityTypeId,
      playerId,
      selectedType?.id ?? 0,
      filesToUpload,
      expDate,
      notes,
      errorHandler
    );
    if (res) {
      successHandler && successHandler('File(s) Added Successfully');
      closeHandler();
    }
    loadingHandler && loadingHandler(false);
  }, [
    loadingHandler,
    expirationDate,
    entityTypeId,
    playerId,
    selectedType,
    filesToUpload,
    notes,
    errorHandler,
    successHandler,
    closeHandler,
  ]);

  return (
    <>
      <Dialog open={open} maxWidth="md" fullWidth>
        <DialogTitle>Add Documents</DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            <div />
            <DocumentTypePicker
              value={selectedType?.id ?? 0}
              entityTypeId={entityTypeId}
              changeHandler={setSelectedType}
            />
            <DatePicker
              label="Expiration Date"
              value={expirationDate}
              onChange={(m) => setExpirationDate(m)}
              slotProps={{ actionBar: { actions: ['clear', 'today', 'cancel', 'accept'] } }}
            />
            <RWTextFieldComponent
              value={notes}
              label="Notes"
              multiline
              rows={5}
              onChange={({ target }) => setNotes(target.value)}
            />
            <FilePicker
              files={filesToUpload}
              addFilesHandler={addFilesCallback}
              removeFileHandler={removeFileCallback}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" color="error" onClick={closeHandler}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="success"
            disabled={filesToUpload.length === 0 || selectedType === null}
            onClick={uploadDocumentsCallback}
          >
            Submit
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
